<script setup lang="ts">
  import { reactive, computed, onMounted, watch } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { UiModalHeader, SelectInput, ColoredButton, ThemeProvider } from '@/shared/ui';
  import { useFunctionalActivityOptions } from '../../lib';
  import type { FunctionalActivity } from '@/entities/FunctionalActivity';
  import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';

  const props = defineProps<{
    title?: string;
    functionalActivity: FunctionalActivity | null;
    isSaving?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'save', data: { sourceId: number }): void;
  }>();

  const {
    functionalActivityOptions,
    isLoading: isLoadingActivities,
    loadFunctionalActivities,
  } = useFunctionalActivityOptions();

  const formData = reactive({
    sourceId: '' as string | number,
  });

  const filteredOptions = computed(() => {
    if (!props.functionalActivity) return functionalActivityOptions.value;
    return functionalActivityOptions.value.filter(
      (opt) => opt.id !== props.functionalActivity?.id?.toString()
    );
  });

  const isFormValid = computed(() => !!formData.sourceId);

  const handleClose = () => {
    emit('close');
  };

  const handleSave = () => {
    if (props.isSaving === true) return;
    if (!isFormValid.value) return;
    emit('save', { sourceId: Number(formData.sourceId) });
  };

  const handleClosed = () => {
    formData.sourceId = '';
  };

  watch(
    () => props.functionalActivity,
    () => {
      formData.sourceId = '';
    }
  );

  onMounted(() => {
    loadFunctionalActivities();
  });
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-[1000px] shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    @closed="handleClosed"
  >
    <ThemeProvider class="flex flex-col">
      <div class="flex flex-col">
        <UiModalHeader
          class="px-6 py-4 text-sm border-b border-gray-02 flex-shrink-0"
          :title="title || 'Clonar Permissões'"
          @close="handleClose"
        />

        <div class="px-6 py-6 space-y-4">
          <div class="flex flex-col gap-1">
            <p class="text-sm text-gray-07 font-semibold">
              Clonar permissões da Atividade:
            </p>
            <p class="text-xs text-gray-05">
              Ao clonar permissões de outra atividade, todas as permissões concedidas
              anteriormente serão removidas para inclusão das novas
            </p>
          </div>

          <div class="flex flex-col gap-2">
            <SelectInput
              v-model="formData.sourceId"
              :options="filteredOptions"
              :disabled="isLoadingActivities"
              :label="isLoadingActivities ? 'Carregando...' : 'Selecione uma Atividade Funcional'"
              container-class="w-auto max-w-xs"
              max-height="110px"
            />
          </div>
        </div>

        <div class="px-6 py-8 flex justify-end gap-3 border-t border-gray-02 flex-shrink-0">
          <ColoredButton
            text="Cancelar"
            outlined
            color="gray-03"
            @click="handleClose"
          />
          <ColoredButton
            :disabled="!isFormValid || isSaving"
            :loading="isSaving"
            text="Salvar"
            :icon="CheckMarkIcon"
            @click="handleSave"
          />
        </div>
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>

<style scoped>
  .vfm__overlay {
    background-color: rgba(0, 0, 0, 0.5);
  }
</style>
